import { useEffect, useState } from 'react';
import { DESKTOP_DOWNLOADS, STORE_LINKS } from '../config';

// Yappyli on the Mac App Store (same listing as iPhone & iPad)
const MAC_APP_STORE = 'https://apps.apple.com/app/id6762079641';

// Yappyli on the Microsoft Store
const MS_STORE = 'https://apps.microsoft.com/detail/9P05S5BS0CSF';

// Desktop is store-only (no .dmg or .exe downloads)
const DESKTOP = {
  ...DESKTOP_DOWNLOADS,
  mac: { name: 'Mac', href: MAC_APP_STORE, store: true, storeName: 'Mac App Store' },
  windows: { name: 'Windows', href: MS_STORE, store: true, storeName: 'Microsoft Store' },
};

// App Store links open in a new tab; installer files download
const linkProps = (d) => (d.store ? { target: '_blank', rel: 'noopener noreferrer' } : { download: true });

function detectDesktopOS() {
  const platform = navigator.userAgentData?.platform || navigator.platform || '';
  const ua = navigator.userAgent || '';
  const s = `${platform} ${ua}`.toLowerCase();

  // Phones and tablets: no desktop build applies
  if (/iphone|ipad|ipod|android/.test(s)) return null;
  // iPadOS reports itself as a Mac, so check for touch
  if (s.includes('mac') && navigator.maxTouchPoints > 1) return null;

  if (s.includes('mac')) return 'mac';
  if (s.includes('win')) return 'windows';
  return null;
}

export default function DownloadPage() {
  const [os, setOs] = useState(null);

  useEffect(() => {
    document.title = "Download Yappyli | iOS, Android, Desktop";
    setOs(detectDesktopOS());
  }, []);

  const primary = os ? DESKTOP[os] : null;
  const others = Object.entries(DESKTOP).filter(([key]) => key !== os);

  return (
    <section className="pt-32 pb-20 px-6 max-w-3xl mx-auto">
      <div className="text-center mb-12">
        <span className="bg-[#EEECFF] text-[#7367ff] px-4 py-1 rounded-full font-bold text-xs uppercase tracking-widest">Get the App</span>
        <h1 className="text-5xl font-extrabold text-[#0F172A] mt-6 mb-4">Download Yappyli</h1>
        <p className="text-xl text-[#64748B] max-w-2xl mx-auto">Available on all your favorite devices. Start chatting happily today.</p>
      </div>

      <div className="space-y-8">
        {/* iOS Card */}
        <div className="bg-white rounded-2xl shadow-lg border border-[#EEECFF] overflow-hidden transition-transform hover:scale-[1.02]">
          <div className="flex flex-col md:flex-row items-center gap-6 p-6">
            <div className="bg-gradient-to-br from-[#E2E8F0] to-[#EEECFF] rounded-2xl w-48 h-56 shrink-0 flex justify-center items-start pt-5 px-5 overflow-hidden">
              <img src="/screens/download-ios.webp" alt="Yappyli on iPhone" width={540} height={1040} loading="lazy" className="w-full h-auto drop-shadow-xl" />
            </div>
            <div className="flex-1 text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start gap-3 mb-3">
                <div className="w-12 h-12 rounded-full bg-[#E2E8F0] flex items-center justify-center text-2xl text-[#0F172A]">
                  <i className="fab fa-apple"></i>
                </div>
                <h3 className="text-2xl font-extrabold text-[#0F172A]">iPhone & iPad</h3>
              </div>
              <p className="text-[#64748B] text-sm mb-4">Requires iOS 15.0 or later • Free</p>
              <a
                href={STORE_LINKS.ios}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-[#7367ff] text-white font-bold py-3 px-8 rounded-full shadow-[0_10px_20px_rgba(115,103,255,0.3)] hover:bg-[#5d4eff] hover:-translate-y-0.5 transition-all"
              >
                Download for iOS
              </a>
            </div>
          </div>
        </div>

        {/* Android Card */}
        <div className="bg-white rounded-2xl shadow-lg border border-[#EEECFF] overflow-hidden transition-transform hover:scale-[1.02]">
          <div className="flex flex-col md:flex-row items-center gap-6 p-6">
            <div className="bg-gradient-to-br from-[#E2E8F0] to-[#EEECFF] rounded-2xl w-48 h-56 shrink-0 flex justify-center items-start pt-5 px-5 overflow-hidden">
              <img src="/screens/download-android.webp" alt="Yappyli on Android" width={540} height={1040} loading="lazy" className="w-full h-auto drop-shadow-xl" />
            </div>
            <div className="flex-1 text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start gap-3 mb-3">
                <div className="w-12 h-12 rounded-full bg-[#E2E8F0] flex items-center justify-center text-2xl text-[#0F172A]">
                  <i className="fab fa-android"></i>
                </div>
                <h3 className="text-2xl font-extrabold text-[#0F172A]">Android</h3>
              </div>
              <p className="text-[#64748B] text-sm mb-4">Requires Android 8.0 or later • Free</p>
              <a
                href={STORE_LINKS.android}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-[#7367ff] text-white font-bold py-3 px-8 rounded-full shadow-[0_10px_20px_rgba(115,103,255,0.3)] hover:bg-[#5d4eff] hover:-translate-y-0.5 transition-all"
              >
                Download for Android
              </a>
            </div>
          </div>
        </div>

        {/* Desktop Card */}
        <div className="bg-white rounded-2xl shadow-lg border border-[#EEECFF] overflow-hidden transition-transform hover:scale-[1.02]">
          <div className="flex flex-col md:flex-row items-center gap-6 p-6">
            <div className="bg-gradient-to-br from-[#E2E8F0] to-[#EEECFF] rounded-2xl w-full md:w-72 shrink-0 flex justify-center items-center p-4">
              <img src="/screens/download-desktop.webp" alt="Yappyli on desktop" width={1200} height={824} loading="lazy" className="w-full h-auto" />
            </div>
            <div className="flex-1 text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start gap-3 mb-3">
                <div className="w-12 h-12 rounded-full bg-[#E2E8F0] flex items-center justify-center text-2xl text-[#0F172A]">
                  <i className="fas fa-desktop"></i>
                </div>
                <h3 className="text-2xl font-extrabold text-[#0F172A]">Desktop</h3>
              </div>

              {/* Primary button for the detected OS */}
              {primary && (
                <a
                  href={primary.href}
                  {...linkProps(primary)}
                  className="inline-block bg-[#7367ff] text-white font-bold py-3 px-8 rounded-full shadow-[0_10px_20px_rgba(115,103,255,0.3)] hover:bg-[#5d4eff] hover:-translate-y-0.5 transition-all"
                >
                  {primary.store ? 'Get it on the Mac App Store' : `Download for ${primary.name}`}
                </a>
              )}


              {/* Other platforms (or all of them if detection found nothing) */}
              <div className={`flex flex-wrap items-center gap-x-4 gap-y-2 justify-center md:justify-start text-sm ${primary ? 'mt-4' : ''}`}>
                {primary && <span className="text-[#94A3B8]">Also available for:</span>}
                {others.map(([key, d]) => (
                  <a key={key} href={d.href} {...linkProps(d)} className="text-[#7367ff] font-bold hover:underline">
                    {primary ? d.name : d.store ? 'Mac App Store' : `Download for ${d.name}`}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-12 text-center text-sm text-[#94A3B8]">
        <p>All versions are completely free. No ads, no tracking.</p>
      </div>
    </section>
  );
}